// Композитный root-provider.
// Подключается один раз в `main.tsx`.
//
// Структура (изнутри наружу):
//   QueryClientProvider — серверный state
//     api.Provider (tRPC React) — типизированный клиент для всех ru/web/admin
//       AuthProvider → PowerSync → Theme → Socket → I18n → Tooltip → {children}
//
// trpcClient создаётся один раз через createTrpcClient() — httpBatchLink на
// /api/trpc + Bearer-токен из auth-storage + 401-интерцептор → AuthExpiredModal.
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { Tooltip as TooltipPrimitive } from "radix-ui";
import { type ReactNode, useState } from "react";
import { api, createTrpcClient } from "../lib/trpc-client";
import { AuthProvider } from "./auth-provider";
import { I18nProvider } from "./i18n-provider";
import { ObservabilityProvider } from "./observability-provider";
import { PowerSyncProvider } from "./powersync-provider";
import { ReconnectRecoveryProvider } from "./reconnect-recovery-provider";
import { SocketProvider } from "./socket-provider";
import { SyncQueueProvider } from "./sync-queue-provider";
import { ThemeProvider } from "./theme-provider";
import { TutorOutboxProvider } from "./tutor-outbox-provider";

interface AppProvidersProps {
	queryClient: QueryClient;
	children: ReactNode;
}

// Глобальные слоты (PastDueBanner / TrialBanner / GlobalCommandPalette /
// UpgradeModal / ManageSubscriptionDialog / GlobalHotkeys с useCmdK) переехали
// внутрь RouterProvider (см. routes/__root.tsx), потому что используют
// useRouterState / useNavigate — TanStack Router-хуки доступны ТОЛЬКО внутри
// RouterProvider, а AppProviders снаружи него.
export function AppProviders({ queryClient, children }: AppProvidersProps) {
	// useState с factory гарантирует один tRPC client на жизненный цикл root —
	// иначе на каждый ре-рендер AppProviders клиент бы пересоздавался.
	const [trpcClient] = useState(() => createTrpcClient());

	return (
		<api.Provider client={trpcClient} queryClient={queryClient}>
			<QueryClientProvider client={queryClient}>
				<ReconnectRecoveryProvider>
					<AuthProvider>
						{/* ObservabilityProvider — ВНУТРИ AuthProvider (нужен useAuth для
            PostHog identify + Hawk affected-user на login/logout).
            Снаружи PowerSync — observability не зависит от sync. */}
						<ObservabilityProvider>
							{/* PowerSync должен быть ВНУТРИ AuthProvider — provider слушает isAuthenticated
            и инициализирует sync только после успешного login (см. powersync-provider.tsx).
            Снаружи Theme/Socket/I18n — чтобы PowerSync сам не блокировал UI shell. */}
							<PowerSyncProvider>
								{/* Видимость upload-очереди PowerSync (счётчик + отброшенные операции). */}
								<SyncQueueProvider>
									{/* Tutor outbox drain — внутри Auth + PowerSync, вне дерева чата. */}
									<TutorOutboxProvider>
										<ThemeProvider>
											<SocketProvider>
												<I18nProvider>
													<TooltipPrimitive.Provider delayDuration={200}>
														{children}
													</TooltipPrimitive.Provider>
												</I18nProvider>
											</SocketProvider>
										</ThemeProvider>
									</TutorOutboxProvider>
								</SyncQueueProvider>
							</PowerSyncProvider>
						</ObservabilityProvider>
					</AuthProvider>
				</ReconnectRecoveryProvider>
			</QueryClientProvider>
		</api.Provider>
	);
}
